import { getUserServiceIds, UserWithServices } from '@orientaction/utils';
import {
  assignmentJournalHasResources,
  AssignmentServiceGroup,
  listAssignmentServiceGroups,
  mergeResourcesMapIntoAssignmentGroups,
} from '../../../AdminUserResultat/shared/serviceResourcesMap';
import locale from '../locale.json';

export const TAB_ALL = 'all';
export const TAB_EBOOK = 'ebook';
export const TAB_GAME = 'seriousgame';

export type ResourceTab = typeof TAB_ALL | typeof TAB_EBOOK | typeof TAB_GAME;

export type ResourceGroup = {
  key: string;
  title: string;
  items: any[];
  orderDate: number;
  isFurtherResources?: boolean;
};

export type SummaryItem = {
  id: string;
  title: string;
  count?: number;
};

const FURTHER_RESOURCES_LABEL = locale.furtherResources;
const RECRUTEMENT_SERVICE = { id: 4, name: 'Recrutement' };

const furtherResourcesService = () => ({
  id: 'further-resources',
  name: FURTHER_RESOURCES_LABEL,
  isFurtherResources: true as const,
});

const getTimestamp = (date?: string) => {
  if (!date) {
    return Number.MAX_SAFE_INTEGER;
  }

  const time = new Date(date).getTime();
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

export const getResourceDate = (resource: any) =>
  resource?.attributes?.DateAttribuate ||
  resource?.DateAttribuate ||
  resource?.date_attribution ||
  resource?.date_assignation ||
  resource?.date_consultation;

const getFallbackService = (resource: any, user?: UserWithServices) => {
  const primaryService = user?.service;
  if (!primaryService || resource?.isNew || resource?.authorizations) {
    return null;
  }

  if (typeof primaryService !== 'object') {
    return { id: primaryService, name: undefined };
  }

  return {
    id: primaryService.id,
    name: primaryService.name || (primaryService as any)?.attributes?.name,
  };
};

const resourceBelongsToUserServices = (resource: any, userServiceIds: number[]) => {
  // The API also returns custom ebooks acquired individually through a share URL.
  if (resource?.isNew) {
    return resource.status !== 'draft';
  }
  if (!userServiceIds.length || !resource?.authorizations?.servicesGame?.length) {
    return true;
  }

  return resource.authorizations.servicesGame.some((service: { id: number }) =>
    userServiceIds.includes(Number(service.id))
  );
};

const getResourceServices = (
  resource: any,
  user?: UserWithServices,
  userServiceIds: number[] = []
) => {
  if (resource?.isRecrutement) {
    return [RECRUTEMENT_SERVICE];
  }

  if (resource?.isNew) {
    const isUnattached =
      (user as { rattachement?: boolean } | undefined)?.rattachement === false ||
      resource.isAuthorizedForCurrentService === false;
    const matchingService = !isUnattached
      ? userServiceIds
          .map(id =>
            resource.authorizations?.servicesGame?.find(
              (service: { id: number }) => Number(service.id) === id
            )
          )
          .find(Boolean)
      : undefined;
    // One category per custom ebook, recalculated from the user's current services.
    return [matchingService || furtherResourcesService()];
  }
  if (resource?.authorizations?.isAttach === false) {
    return [{ id: 'further-resources', name: FURTHER_RESOURCES_LABEL, isFurtherResources: true }];
  }

  if (resource?.authorizations?.servicesGame?.length > 0) {
    if (userServiceIds.length) {
      return resource.authorizations.servicesGame.filter((service: { id: number }) =>
        userServiceIds.includes(Number(service.id))
      );
    }

    return resource.authorizations.servicesGame;
  }

  return [getFallbackService(resource, user)].filter(Boolean);
};

type UserWithAssignments = UserWithServices & {
  service_assignments?: any;
  service_resources_map?: any;
};

const toPositiveId = (value: any): number | null => {
  const id = Number(typeof value === 'object' ? value?.id : value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const getGameId = (resource: any) => toPositiveId(resource?.idGame);

const getEbookId = (resource: any) =>
  resource?.isNew ? toPositiveId(resource?.idEbook) : null;

const getBookIds = (resource: any): number[] => {
  if (resource?.isNew || resource?.idGame) {
    return [];
  }

  return Array.from(
    new Set(
      [
        toPositiveId(resource?.attributes?.book_template?.id ?? resource?.attributes?.book_template),
        toPositiveId(resource?.book_template?.id ?? resource?.book_template),
        toPositiveId(resource?.attributes?.id),
        toPositiveId(resource?.id),
      ].filter((id): id is number => id != null)
    )
  );
};

const getAssignmentServicesForResource = (
  resource: any,
  assignmentGroups: AssignmentServiceGroup[]
) => {
  const gameId = getGameId(resource);
  const ebookId = getEbookId(resource);
  const bookIds = getBookIds(resource);

  return assignmentGroups
    .filter(group => {
      if (gameId && group.gameIds.has(gameId)) {
        return true;
      }
      if (ebookId && group.ebookIds.has(ebookId)) {
        return true;
      }
      return bookIds.some(id => group.bookIds.has(id) || group.ebookIds.has(id));
    })
    .map(group => ({ id: group.id, name: group.name }));
};

const pushResourceIntoGroups = (
  groups: Map<string, ResourceGroup>,
  resource: any,
  services: Array<{ id?: number | string; name?: string; isFurtherResources?: boolean }>
) => {
  const resourceDate = getTimestamp(getResourceDate(resource));

  services.forEach(service => {
    const isFurtherResources = service.isFurtherResources === true;
    const title = isFurtherResources ? FURTHER_RESOURCES_LABEL : service.name;
    const key = isFurtherResources ? 'further-resources' : `service-${service.id || title}`;

    if (!title) {
      return;
    }

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        title,
        items: [],
        orderDate: resourceDate,
        isFurtherResources,
      });
    }

    const group = groups.get(key) as ResourceGroup;
    group.items.push(resource);
    group.orderDate = Math.min(group.orderDate, resourceDate);
  });
};

const sortResourceGroups = (groups: Map<string, ResourceGroup>) =>
  Array.from(groups.values()).sort((a, b) => {
    if (a.isFurtherResources && !b.isFurtherResources) {
      return 1;
    }

    if (!a.isFurtherResources && b.isFurtherResources) {
      return -1;
    }

    return a.orderDate - b.orderDate;
  });

const shouldSkipDuplicateCustomEbook = (resource: any, seenCustomEbooks: Set<string>) => {
  if (!resource?.isNew || resource.idEbook == null) {
    return false;
  }
  const ebookId = String(resource.idEbook);
  if (seenCustomEbooks.has(ebookId)) {
    return true;
  }
  seenCustomEbooks.add(ebookId);
  return false;
};

const groupByCatalogAuthorizations = (
  resources: any[],
  user?: UserWithServices
): ResourceGroup[] => {
  const groups = new Map<string, ResourceGroup>();
  const userServiceIds = getUserServiceIds(user);
  const seenCustomEbooks = new Set<string>();

  (resources || [])
    .filter((resource: any) => resourceBelongsToUserServices(resource, userServiceIds))
    .forEach((resource: any) => {
      if (shouldSkipDuplicateCustomEbook(resource, seenCustomEbooks)) {
        return;
      }
      pushResourceIntoGroups(
        groups,
        resource,
        getResourceServices(resource, user, userServiceIds)
      );
    });

  return sortResourceGroups(groups);
};

const readServiceName = (value: any): string | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  return value.name || value.attributes?.name;
};

const labelAssignmentGroups = (
  groups: AssignmentServiceGroup[],
  user?: UserWithAssignments
): AssignmentServiceGroup[] => {
  const names = new Map<number, string>();
  const register = (value: any) => {
    const id = toPositiveId(value);
    const name = readServiceName(value);
    if (id && name) {
      names.set(id, name);
    }
  };
  register(user?.service);
  if (Array.isArray(user?.services)) {
    user?.services?.forEach(register);
  } else {
    register(user?.services);
  }

  return groups.map(group => ({
    ...group,
    name: names.get(group.id) || group.name,
  }));
};

const groupByAssignments = (
  resources: any[],
  user: UserWithServices | undefined,
  assignmentGroups: AssignmentServiceGroup[]
): ResourceGroup[] => {
  const groups = new Map<string, ResourceGroup>();
  const userServiceIds = getUserServiceIds(user);
  const seenCustomEbooks = new Set<string>();

  (resources || []).forEach((resource: any) => {
    if (shouldSkipDuplicateCustomEbook(resource, seenCustomEbooks)) {
      return;
    }

    if (resource?.isRecrutement) {
      pushResourceIntoGroups(
        groups,
        resource,
        getResourceServices(resource, user, userServiceIds)
      );
      return;
    }

    const assignmentServices = getAssignmentServicesForResource(resource, assignmentGroups);
    if (assignmentServices.length) {
      pushResourceIntoGroups(groups, resource, assignmentServices);
    }
  });

  return sortResourceGroups(groups);
};

/**
 * Group resources by the connected user's `service_assignments` journal.
 * The recrutement ebook keeps its catalog category. If the journal has no
 * resource ids, the previous catalog grouping is kept (legacy accounts).
 */
export const getGroupedResources = (
  resources: any[],
  user?: UserWithAssignments,
  assignments?: any
): ResourceGroup[] => {
  const assignmentGroups = labelAssignmentGroups(
    mergeResourcesMapIntoAssignmentGroups(
      listAssignmentServiceGroups(assignments ?? user?.service_assignments),
      user?.service_resources_map
    ),
    user
  );

  if (assignmentJournalHasResources(assignmentGroups)) {
    return groupByAssignments(resources, user, assignmentGroups);
  }

  return groupByCatalogAuthorizations(resources, user);
};

export const flattenGroups = (groups: ResourceGroup[]) =>
  groups.reduce((acc: any[], group) => acc.concat(group.items), []);

export const flattenGroupEntries = (groups: ResourceGroup[]) =>
  groups.reduce(
    (acc: any[], group) =>
      acc.concat(
        group.items.map((item: any) => ({
          item,
          groupKey: group.key,
          groupTitle: group.title,
        }))
      ),
    []
  );

export const getSectionId = (type: ResourceTab, groupKey: string) => `${type}-${groupKey}`;

export const getMobileCardId = (type: ResourceTab, groupKey: string, itemId: string | number) =>
  `mobile-${type}-${groupKey}-${itemId}`;

export const normalizeRedirectTab = (tab?: string): ResourceTab => {
  if (tab === 'ebooks' || tab === TAB_EBOOK) {
    return TAB_EBOOK;
  }

  if (tab === TAB_GAME) {
    return TAB_GAME;
  }

  return TAB_ALL;
};
